import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import type {
  CuttysarkDivision,
  CuttysarkLiveView,
} from '@cortex/domain-product-innovation-lab-types';
import { useUpdateCuttysarkSession } from '../../../hooks/useCuttysark.js';
import { ApiError } from '../../../lib/api/_request.js';
import {
  NEW_DIVISION,
  newCandidateRow,
  parseIntegerInput,
  setupDraftFromView,
  setupRequestFromDraft,
  type CandidateRowDraft,
  type SetupDraft,
} from './setup-draft.js';
import { CandidatePhotoUpload } from './CandidatePhotoUpload.js';

/** @graph-connects none */
interface SetupEditorProps {
  sessionId: string;
  view: CuttysarkLiveView;
  /** 他の運営に追い越されたとき（409）。親が最新を読み直してフォームを作り直す。 */
  onConflict: () => void;
}

/** @graph-connects none */
const TEXTAREA_CLASS =
  'mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 text-sm text-[var(--color-text-primary)]';

/**
 * 運営がセッションの設定（タイトル・部門・候補者・投票者・運営）を編集して保存するフォーム。
 * 保存はリビジョン付きで、他の運営に先を越されたら onConflict で最新を読み直してもらう。
 * 入力の検証は API が行い、弾かれたときのメッセージをそのまま出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの部門・候補者（二つ名・紹介文・資料・写真）・投票者・運営を設定する
 * @graph-connects CandidatePhotoUpload [calls] 候補者の写真のアップロード
 * @graph-connects useUpdateCuttysarkSession [calls] 設定の保存
 * @graph-connects setupDraftFromView [calls] 現在の設定から下書きを作る
 * @graph-connects setupRequestFromDraft [calls] 下書きを更新リクエストにする
 */
export function SetupEditor({ sessionId, view, onConflict }: SetupEditorProps): ReactNode {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SetupDraft>(() => setupDraftFromView(view));
  const update = useUpdateCuttysarkSession(sessionId);

  const setDivision = (index: number, patch: Partial<CuttysarkDivision>): void => {
    setDraft((prev) => ({
      ...prev,
      divisions: prev.divisions.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    }));
  };
  const setCandidate = (index: number, patch: Partial<CandidateRowDraft>): void => {
    setDraft((prev) => ({
      ...prev,
      candidates: prev.candidates.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));
  };
  const divisionOptions = draft.divisions.map((d) => ({ value: d.id, label: d.name || d.id }));

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(setupRequestFromDraft(draft, view.revision), {
          onError: (error) => {
            if (error instanceof ApiError && error.status === 409) onConflict();
          },
        });
      }}
    >
      <Input
        label={t('cuttysark.operate.sessionTitle')}
        fullWidth
        value={draft.title}
        onChange={(e) => {
          setDraft((prev) => ({ ...prev, title: e.target.value }));
        }}
      />
      <Input
        size="sm"
        type="url"
        label={t('cuttysark.operate.meetUrl')}
        placeholder="https://meet.google.com/xxx-xxxx-xxx"
        value={draft.meetUrl}
        onChange={(e) => {
          setDraft((prev) => ({ ...prev, meetUrl: e.target.value }));
        }}
      />

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('cuttysark.operate.divisions')}
        </h3>
        {draft.divisions.map((division, index) => (
          // 部門 ID 自体を編集するため、行の識別には並び順を使う
          <div key={index} className="grid grid-cols-2 items-end gap-2 md:grid-cols-5">
            <Input
              size="sm"
              label={t('cuttysark.operate.divisionId')}
              value={division.id}
              onChange={(e) => {
                setDivision(index, { id: e.target.value });
              }}
            />
            <Input
              size="sm"
              label={t('cuttysark.operate.divisionName')}
              value={division.name}
              onChange={(e) => {
                setDivision(index, { name: e.target.value });
              }}
            />
            <Input
              size="sm"
              type="number"
              label={t('cuttysark.operate.votesPerVoter')}
              value={division.votesPerVoter}
              onChange={(e) => {
                setDivision(index, { votesPerVoter: parseIntegerInput(e.target.value) });
              }}
            />
            <Input
              size="sm"
              type="number"
              label={t('cuttysark.operate.advanceCount')}
              value={division.advanceCount}
              onChange={(e) => {
                setDivision(index, { advanceCount: parseIntegerInput(e.target.value) });
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft((prev) => ({
                  ...prev,
                  divisions: prev.divisions.filter((_, i) => i !== index),
                }));
              }}
            >
              {t('cuttysark.common.remove')}
            </Button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            setDraft((prev) => ({ ...prev, divisions: [...prev.divisions, { ...NEW_DIVISION }] }));
          }}
        >
          {t('cuttysark.operate.addDivision')}
        </Button>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('cuttysark.operate.candidates')}
        </h3>
        {draft.candidates.map((candidate, index) => (
          // メール自体を編集するため、行の識別には並び順を使う
          <div
            key={index}
            className="grid grid-cols-1 gap-2 rounded-md border border-[var(--color-border)] p-2 md:grid-cols-3"
          >
            <Input
              size="sm"
              type="email"
              label={t('cuttysark.operate.candidateEmail')}
              value={candidate.email}
              onChange={(e) => {
                setCandidate(index, { email: e.target.value });
              }}
            />
            <Select
              size="sm"
              label={t('cuttysark.operate.candidateDivision')}
              options={divisionOptions}
              value={candidate.divisionId}
              onChange={(e) => {
                setCandidate(index, { divisionId: e.target.value });
              }}
            />
            <Input
              size="sm"
              label={t('cuttysark.operate.candidateTitle')}
              value={candidate.title}
              onChange={(e) => {
                setCandidate(index, { title: e.target.value });
              }}
            />
            <Input
              size="sm"
              label={t('cuttysark.operate.materialUrl')}
              value={candidate.materialUrl}
              onChange={(e) => {
                setCandidate(index, { materialUrl: e.target.value });
              }}
            />
            <Input
              size="sm"
              label={t('cuttysark.operate.photoUrl')}
              value={candidate.photoUrl}
              onChange={(e) => {
                setCandidate(index, { photoUrl: e.target.value });
              }}
            />
            <div className="md:col-span-3">
              <CandidatePhotoUpload
                sessionId={sessionId}
                candidate={view.candidates.find(
                  (saved) => saved.email === candidate.email.trim().toLowerCase(),
                )}
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft((prev) => ({
                    ...prev,
                    candidates: prev.candidates.filter((_, i) => i !== index),
                  }));
                }}
              >
                {t('cuttysark.common.remove')}
              </Button>
            </div>
            <label className="text-sm text-[var(--color-text-secondary)] md:col-span-3">
              {t('cuttysark.operate.candidateIntro')}
              <Textarea
                className={TEXTAREA_CLASS}
                rows={2}
                value={candidate.intro}
                onChange={(e) => {
                  setCandidate(index, { intro: e.target.value });
                }}
              />
            </label>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            setDraft((prev) => ({
              ...prev,
              candidates: [...prev.candidates, newCandidateRow(prev.divisions)],
            }));
          }}
        >
          {t('cuttysark.operate.addCandidate')}
        </Button>
      </section>

      <section className="space-y-2">
        <Select
          label={t('cuttysark.operate.voterMode')}
          options={[
            { value: 'listed', label: t('cuttysark.operate.voterModeListed') },
            { value: 'anyone', label: t('cuttysark.operate.voterModeAnyone') },
          ]}
          value={draft.voterMode}
          onChange={(e) => {
            const voterMode = e.target.value === 'anyone' ? 'anyone' : 'listed';
            setDraft((prev) => ({ ...prev, voterMode }));
          }}
        />
        {draft.voterMode === 'listed' && (
          <label className="block text-sm text-[var(--color-text-secondary)]">
            {t('cuttysark.operate.voters')}
            <Textarea
              className={TEXTAREA_CLASS}
              rows={5}
              value={draft.votersText}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, votersText: e.target.value }));
              }}
            />
          </label>
        )}
        <label className="block text-sm text-[var(--color-text-secondary)]">
          {t('cuttysark.operate.operators')}
          <Textarea
            className={TEXTAREA_CLASS}
            rows={2}
            value={draft.operatorsText}
            onChange={(e) => {
              setDraft((prev) => ({ ...prev, operatorsText: e.target.value }));
            }}
          />
        </label>
      </section>

      {update.isError && !(update.error instanceof ApiError && update.error.status === 409) && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {update.error.message}
        </p>
      )}
      {update.isSuccess && (
        <p className="text-sm text-[var(--color-success)]" role="status">
          {t('cuttysark.common.saved')}
        </p>
      )}
      <Button type="submit" loading={update.isPending}>
        {t('cuttysark.operate.save')}
      </Button>
    </form>
  );
}
